import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * Splits a top-bar's entries into the ones that fit and the ones that must move
 * into an overflow menu ("priority plus").
 *
 * WHY NOT `overflow-x-auto`. That was the obvious fix and it is the wrong one:
 * the bar's entries own flyouts that escape their bounds, and a scroll container
 * clips them — the dropdown would be cut off at the bar's own edge. This was
 * recorded as open item 5 in docs/pages-content-frontend-handover.md §9 for
 * exactly that reason. Moving entries out of the bar keeps every flyout in a
 * container that never clips.
 *
 * HOW THE MEASUREMENT WORKS. Entries that have been moved into the overflow
 * menu are no longer in the bar, so their width can't be read back — which would
 * make the split sticky: once an entry overflowed it could never return on a
 * widening viewport. The caller therefore renders a second, hidden copy of the
 * full list and points `measureRef` at it. That copy always holds every entry at
 * its natural width, so growing and shrinking are symmetric.
 */

/** Matches the `gap-1` between bar entries. Used to price the spaces between them. */
const GAP_PX = 4;

/** Fallback width for the overflow trigger before it has ever rendered. */
const FALLBACK_MORE_PX = 96;

interface BarOverflow<T> {
  /** The row the visible entries live in — its width is the budget. */
  containerRef: React.RefObject<HTMLUListElement | null>;
  /** The hidden full-list copy the natural widths are read from. */
  measureRef: React.RefObject<HTMLUListElement | null>;
  /** The overflow trigger, so its width can be reserved rather than guessed. */
  moreRef: React.RefObject<HTMLLIElement | null>;
  visible: T[];
  overflow: T[];
}

export const useBarOverflow = <T,>(items: T[]): BarOverflow<T> => {
  const containerRef = useRef<HTMLUListElement>(null);
  const measureRef = useRef<HTMLUListElement>(null);
  const moreRef = useRef<HTMLLIElement>(null);
  const [visibleCount, setVisibleCount] = useState(items.length);

  const measure = useCallback((): void => {
    const container = containerRef.current;
    const source = measureRef.current;
    if (!container || !source) return;

    const widths = Array.from(source.children).map((child) => child.getBoundingClientRect().width);
    const budget = container.clientWidth;
    if (!budget || !widths.length) return;

    const totalIfAllShown = widths.reduce((sum, w) => sum + w, 0) + GAP_PX * (widths.length - 1);

    if (totalIfAllShown <= budget) {
      setVisibleCount(widths.length);
      return;
    }

    // Something must move, so the trigger's own width has to be paid for first.
    const moreWidth = moreRef.current?.getBoundingClientRect().width ?? FALLBACK_MORE_PX;
    let used = moreWidth + GAP_PX;
    let fits = 0;

    for (const width of widths) {
      const next = used + width + (fits > 0 ? GAP_PX : 0);
      if (next > budget) break;
      used = next;
      fits += 1;
    }

    setVisibleCount(fits);
  }, []);

  // Re-measure before paint on any change to the entries themselves.
  useLayoutEffect(measure, [measure, items]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // rAF-batched: ResizeObserver fires during layout, and setting state from
    // inside it synchronously trips "loop completed with undelivered
    // notifications" in Chrome.
    let frame = 0;
    const schedule = (): void => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    };

    const observer = new ResizeObserver(schedule);
    observer.observe(container);

    // Web fonts land after first paint and change every entry's width, so the
    // first measurement is taken against fallback metrics and must be redone.
    let cancelled = false;
    document.fonts?.ready.then(() => {
      if (!cancelled) schedule();
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [measure]);

  return {
    containerRef,
    measureRef,
    moreRef,
    visible: items.slice(0, visibleCount),
    overflow: items.slice(visibleCount),
  };
};
